import { useI18n } from '@ecom/i18n';
import { type ReactElement, type ReactNode, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { isSubmenu, NAV_MENU, NAV_SECTION_STARTS, type NavSubmenu, submenuOf } from './nav';
import { NavIcon } from './nav-icons';

import { useActionableOrdersCountQuery } from '@/features/dashboard/api';
import { cn } from '@/lib/cn';

function Chevron(): ReactElement {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="13"
      height="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

/**
 * « Commandes ▸ » — folds its screens; open while one of them is the current page, otherwise on a click. In the
 * collapsed rail it is a single icon that opens the first screen of the domain (the rail has no room for a fold).
 */
function Submenu({
  entry,
  open,
  collapsed,
  badge,
  onToggle,
  onNavigate,
}: {
  entry: NavSubmenu;
  open: boolean;
  collapsed: boolean;
  badge: number;
  onToggle: () => void;
  onNavigate: () => void;
}): ReactElement {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const containsCurrent = entry.children.some((child) => child.to === pathname);
  const panelId = `submenu-${entry.id.replace(/\W/gu, '')}`;
  const label = t(entry.labelKey);
  const badgeNode = badge > 0 ? <em className="sk-badge not-italic">{badge}</em> : null;

  if (collapsed) {
    return (
      <li>
        <NavLink
          to={entry.id}
          onClick={onNavigate}
          title={label}
          aria-label={label}
          className={cn('sk-nav-item', containsCurrent && 'is-active')}
        >
          <NavIcon to={entry.id} />
          <span className="sk-nav-label">{label}</span>
        </NavLink>
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
        className={cn('sk-nav-item', containsCurrent && !open && 'is-active')}
      >
        <NavIcon to={entry.id} />
        <span className="sk-nav-label">{label}</span>
        {badgeNode}
        <span
          className={cn('sk-nav-chevron', badge > 0 && 'ms-1')}
          style={{ transform: open ? 'rotate(90deg)' : undefined }}
        >
          <Chevron />
        </span>
      </button>
      <div id={panelId} className={cn('sk-sub', open && 'is-open')}>
        <ul>
          {entry.children.map((child) => (
            <li key={child.to}>
              <NavLink
                to={child.to}
                onClick={onNavigate}
                tabIndex={open ? undefined : -1}
                className={({ isActive }) => cn('sk-nav-item', isActive && 'is-active')}
                end
              >
                <span className="sk-nav-label">{t(child.labelKey)}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
}

/**
 * The sidebar of the Figma design (« Souk 2.0 »): ABOUEB navy (a night navy in dark), the brand row, uppercase group labels,
 * the current screen on an ice wash with a sky-blue bar at its edge, a « Réduire le menu » control that folds it to a
 * 74px icon rail (each icon keeps its name as a tooltip), and — below 900px — the same panel sliding in as a drawer
 * from the menu button. The menu itself (`nav.ts`: entries, order, sub-menus, routes) is unchanged: the design changes
 * how it looks, never where it leads.
 *
 * The count beside « Commandes » is the real number of orders waiting for the merchant (the same count the dashboard
 * and the to-do screen use); it is not shown at zero.
 */
export function Sidebar({
  collapsed,
  onToggleCollapsed,
  mobileOpen,
  onCloseMobile,
}: {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}): ReactElement {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const actionable = useActionableOrdersCountQuery();
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(() => {
    const current = submenuOf(pathname);
    return new Set(current === undefined ? [] : [current.id]);
  });

  useEffect(() => {
    const current = submenuOf(pathname);
    if (current === undefined) return;
    setOpenIds((ids) => (ids.has(current.id) ? ids : new Set([...ids, current.id])));
  }, [pathname]);

  const toggle = (id: string): void => {
    setOpenIds((ids) => {
      const next = new Set(ids);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const sectionTitle = (key: string): ReactNode => {
    const title = NAV_SECTION_STARTS[key];
    if (title === undefined) return null;
    return (
      <li key={`section-${key}`} aria-hidden="true" className="sk-group-label">
        {t(title)}
      </li>
    );
  };

  return (
    <>
      {mobileOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-[45] bg-[rgb(7_8_13/0.5)] backdrop-blur-[3px] min-[901px]:hidden"
          onClick={onCloseMobile}
          aria-label={t('merchant.topbar.menuClose')}
        />
      ) : null}
      <aside
        className={cn('sk-sidebar', collapsed && 'is-collapsed', mobileOpen && 'is-mobile-open')}
        aria-label={t('merchant.sidebar.navLabel')}
      >
        <div className="sk-brand">
          {/* The ABOUEB logo on its own white tile (the artwork has a white ground), cropped on the circle and the
              wordmark so it stays legible at sidebar size; the rail keeps only the tile. */}
          <span className="sk-brand-logo">
            <img src={`${import.meta.env.BASE_URL}aboueb-logo.jpg`} alt="" />
          </span>
          <div className="sk-brand-copy">
            <strong>ABOUEB</strong>
            <span>{t('merchant.sidebar.subtitle')}</span>
          </div>
          <button
            type="button"
            onClick={onCloseMobile}
            aria-label={t('merchant.topbar.menuClose')}
            className="ms-auto flex h-9 w-9 items-center justify-center rounded-[10px] border border-white/10 text-[#c9d8ee] hover:text-white min-[901px]:hidden"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            >
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
        </div>

        <nav className="sk-nav" aria-label={t('merchant.sidebar.navLabel')}>
          <ul className="flex flex-col gap-0.5">
            {NAV_MENU.flatMap((entry) => [
              sectionTitle(isSubmenu(entry) ? entry.id : entry.to),
              isSubmenu(entry) ? (
                <Submenu
                  key={entry.id}
                  entry={entry}
                  open={openIds.has(entry.id)}
                  collapsed={collapsed}
                  badge={entry.id === '/orders' ? actionable.count : 0}
                  onToggle={() => {
                    toggle(entry.id);
                  }}
                  onNavigate={onCloseMobile}
                />
              ) : (
                <li key={entry.to}>
                  <NavLink
                    to={entry.to}
                    onClick={onCloseMobile}
                    title={collapsed ? t(entry.labelKey) : undefined}
                    className={({ isActive }) => cn('sk-nav-item', isActive && 'is-active')}
                    end
                  >
                    <NavIcon to={entry.to} />
                    <span className="sk-nav-label">{t(entry.labelKey)}</span>
                  </NavLink>
                </li>
              ),
            ])}
          </ul>
        </nav>

        <div className="sk-sidebar-foot">
          <button
            type="button"
            className="sk-collapse"
            onClick={onToggleCollapsed}
            aria-pressed={collapsed}
            title={collapsed ? t('merchant.sidebar.expand') : undefined}
          >
            <Chevron />
            <span>{t(collapsed ? 'merchant.sidebar.expand' : 'merchant.sidebar.collapse')}</span>
          </button>
        </div>
      </aside>
    </>
  );
}
